<template>
  <div>
    <div v-if="triggers.length" class="action-key-container">
      <label class="tb-label">
        Triggers
      </label>

      <p>
        Trigger by pressing buttons or using keyboard shortcuts
      </p>

      <div class="action-keys">
        <action-key
          v-for="(trigger, index) in triggers"
          :key="index"
          :action="trigger"
          @trigger="onTrigger"
        />
      </div>
    </div>

    <div v-if="!isScorestream" class="tb-form-group">
      <label class="tb-label">
        URL
      </label>

      <input
        type="text"
        class="tb-input"
        placeholder="https://example.com"
        v-model="url"
        :class="{ 'is-invalid': url && !isValidUrl }"
        name="url"
        :disabled="isExperience"
        data-entity="graphics-frame-url"
        data-test="graphics-frame-url"
      />

      <span v-show="!isValidUrl" class="text-danger">Invalid url format</span>

      <small v-if="isExperience">
        Unable to update Browser Source URL when it is tied to an Experience.
      </small>
    </div>

    <template v-if="isScorestream">
      <div class="tb-form-group">
        <label class="tb-label">
          Widget Type
        </label>

        <select
          v-model="widgetType"
          class="tb-input"
          data-entity="graphics-frame-widgetType"
          data-test="graphics-frame-widgetType"
        >
          <option value="vert">
            Vertical Scoreboard
          </option>

          <option value="banner">
            Banner Scoreboard
          </option>

          <option value="teamSchedule">
            Team Schedule
          </option>
        </select>
      </div>

      <div class="tb-form-group">
        <label class="tb-label">
          Widget ID
        </label>

        <input
          v-model="widgetId"
          type="text"
          inputmode="numeric"
          pattern="[0-9]*"
          class="tb-input"
          :class="{ 'is-invalid': widgetId && !isValidWidgetId }"
          data-entity="graphics-frame-widgetId"
          data-test="graphics-frame-widgetId"
          @input="validateNumericInput"
        />

        <span v-show="widgetId && !isValidWidgetId" class="text-danger">Widget ID must be a number</span>
      </div>
    </template>

    <div class="tb-form-group">
      <label class="tb-label">
        Background Color
      </label>

      <div class="flex items-center margin-bottom-sm">
        <div class="tb-radio margin-right-sm">
          <input
            type="radio"
            id="solid"
            value="solid"
            v-model="fillStyle"
            data-entity="producer-editor-radioFillStyle"
            data-test="graphics-editor-fill-solid"
          />

          <label class="radio inline-radio" for="solid">Solid</label>
        </div>

        <div class="tb-radio">
          <input
            type="radio"
            id="gradient"
            value="gradient"
            v-model="fillStyle"
            data-entity="producer-editor-radioFillStyle"
            data-test="graphics-editor-fill-gradient"
          >

          <label class="radio inline-radio" for="gradient">Gradient</label>
        </div>
      </div>

      <template v-if="fillStyle === 'solid'">
        <div
          v-if="fillStyle == 'solid'"
          class="colorpicker"
          data-entity="producer-editor-toggleBackgroundColorPicker"
          data-test="graphics-editor-background-color"
        >
          <colorpicker
            :color="backgroundColor"
            data-test="graphics-editor-background-color"
            @change="updateField('backgroundColor', $event)"
          />
        </div>
      </template>

      <template v-else>
        <div class="tb-form-group flex items-center">
          <div class="input-group">
            <input
              class="tb-input"
              type="number"
              name="gradientDirection"
              v-model.number="gradientDirection"
              data-entity="producer-editor-gradientDirection"
              data-test="graphics-editor-gradient-direction"
            />

            <span class="input-group-addon symbol">°</span>
          </div>

          <span class="margin-left-sm" data-entity="producer-editor-toggleGradientColorOnePicker">
            <colorpicker
              :color="gradientColorOne"
              data-test="graphics-editor-gradient-color-one"
              @change="updateField('gradientColorOne', $event)"
            />
          </span>

          <span class="margin-left-sm" data-entity="producer-editor-toggleGradientColorTwoPicker">
            <colorpicker
              :color="gradientColorTwo"
              data-test="graphics-editor-gradient-color-two"
              @change="updateField('gradientColorTwo', $event)"
            />
          </span>
        </div>
      </template>
    </div>

    <div class="tb-form-group">
      <div class="tb-checkbox">
        <input
          type="checkbox"
          id="backgroundImage"
          v-model="backgroundImage"
          data-entity="producer-editor-toggleBackgroundImage"
          data-test="graphics-editor-background-image"
        />

        <label class="checkbox" for="backgroundImage">Background image</label>
      </div>
    </div>

    <div v-if="backgroundImage">
      <div class="tb-form-group">
        <div v-if="backgroundImageUrl" class="media-upload-wrapper">
          <div
            class="media"
            :style="{
                backgroundImage: `url('${backgroundImageUrl}')`,
              }"
          />

          <div class="edit-media">
            <button
              class="tb-btn tb-btn-contained on-image"
              data-entity="producer-editor-editBackgroundImage"
              data-test="graphics-editor-backgroundImage-select"
              @click="selectAsset"
            >
              <span class="icon icon-pencil"></span>
            </button>
          </div>
        </div>

        <button
          v-else
          class="tb-btn tb-btn-contained medium-em"
          data-entity="producer-editor-selectBackgroundImage"
          data-test="graphics-editor-backgroundImage-select"
          @click="selectAsset"
        >
          <span class="icon icon-picture margin-right-xxs"></span>
          Choose image
        </button>
      </div>

      <div v-if="backgroundImageUrl" class="tb-form-group">
        <div class="tb-radio">
          <input
            type="radio"
            id="cover"
            value="cover"
            v-model="backgroundSize"
            data-entity="producer-editor-radioBackgroundSize"
            data-test="graphics-editor-backgroundSize-cover"
          />

          <!-- NOTE: Adding manual click handler because of weird scroll bug I can't fix yet -->
          <label
            class="radio inline-radio"
            for="cover"
            @click.prevent="backgroundSize = 'cover'"
          >
            Fill image
          </label>
        </div>

        <div class="tb-radio">
          <input
            type="radio"
            id="contain"
            value="contain"
            v-model="backgroundSize"
            data-entity="producer-editor-radioBackgroundSize"
            data-test="graphics-editor-backgroundSize-fit"
          />

          <!-- NOTE: Adding manual click handler because of weird scroll bug I can't fix yet -->
          <label
            class="radio inline-radio"
            for="contain"
            @click.prevent="backgroundSize = 'contain'"
          >
            Fit image
          </label>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Partners from '../../../../services/partners';

import ActionKey from '../../../panel-editor/hotkeys/ActionKey.vue';

export default {
  components: {
    ActionKey,
  },

  props: {
    graphic: {
      type: Object,
      required: true,
    },

    element: {
      type: Object,
      required: true,
    },

    graphic_fields: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      experience: null,
    };
  },

  mounted() {
    this.getExperience();
  },

  computed: {
    triggers() {
      const hardcodedTriggers = [
        {
          type: 'frame-trigger',
          display: 'Refresh',
          hotkey: 'R',
          value: 'refresh',
        },
      ];

      const customTriggers = (this.experience?.triggers || []).map((trigger, idx) => ({
        type: 'frame-trigger',
        display: trigger.label,
        hotkey: `${idx + 1}`,
        value: trigger.key,
      }));

      return [...hardcodedTriggers, ...customTriggers];
    },

    isExperience() {
      return !!this.element.experienceId;
    },

    isScorestream() {
      return this.element.isScorestream === true;
    },

    url: {
      get() {
        return this.elementFields?.url ?? this.element.url ?? '';
      },

      set(value) {
        this.updateField('url', value);
      },
    },

    widgetType: {
      get() {
        return this.elementFields?.widgetType ?? this.element.widgetType ?? 'vert';
      },

      set(value) {
        this.updateField('widgetType', value);
        this.updateScorestreamUrl();
      },
    },

    widgetId: {
      get() {
        return this.elementFields?.widgetId ?? this.element.widgetId ?? '';
      },

      set(value) {
        this.updateField('widgetId', value);
        this.updateScorestreamUrl();
      },
    },

    isValidWidgetId() {
      if (!this.widgetId) {
        return true;
      }
      return /^\d+$/.test(String(this.widgetId));
    },

    currentLayoutIndex() {
      return this.$store.state.production.production.index;
    },

    isCurrentLayout() {
      return this.graphic.timeline_id === this.currentLayoutIndex;
    },

    currentSampleLayoutIndex() {
      return this.$store.state.preview.production.index;
    },

    isCurrentSampleLayout() {
      return this.graphic.timeline_id === this.currentSampleLayoutIndex;
    },

    type() {
      return this.element.type;
    },

    backgroundColor() {
      return this.elementFields?.backgroundColor ?? this.element.backgroundColor ?? '#0F84FB';
    },

    gradientColorOne() {
      return this.elementFields?.gradientColorOne ?? this.element.gradientColorOne ?? '#00c4dd';
    },

    gradientColorTwo() {
      return this.elementFields?.gradientColorTwo ?? this.element.gradientColorTwo ?? '#0f84fb';
    },

    backgroundImage: {
      get() {
        return this.elementFields?.backgroundImage ?? this.element.backgroundImage ?? false;
      },

      set(value) {
        this.updateField('backgroundImage', value);
      },
    },

    backgroundSize: {
      get() {
        return this.elementFields?.backgroundSize ?? this.element.backgroundSize ?? 'cover';
      },

      set(value) {
        this.updateField('backgroundSize', value);
      },
    },

    backgroundImageUrl() {
      return this.elementFields?.backgroundImageUrl ?? this.element.backgroundImageUrl ?? '';
    },

    fillStyle: {
      get() {
        return this.elementFields?.fillStyle ?? this.element.fillStyle ?? 'solid';
      },

      set(value) {
        this.updateField('fillStyle', value);
      },
    },

    gradientDirection: {
      get() {
        // TODO: Replace magic number
        return this.elementFields?.gradientDirection ?? this.element.gradientDirection ?? 180;
      },

      set(value) {
        this.updateField('gradientDirection', value);
      },
    },

    elementFields() {
      return this.graphic_fields[this.element.id];
    },

    isValidUrl() {
      if (!this.url) {
        return true;
      }

      try {
        const urlObj = new URL(this.url);
        return urlObj.protocol === 'https:';
      } catch {
        return false;
      }
    },
  },

  methods: {
    async getExperience() {
      if (!this.element.experienceId) {
        return;
      }

      const { partner, experienceId } = this.element;
      this.experience = await Partners.getExperience(partner, experienceId).catch((err) => {
        console.error('Failed to fetch Partner Experience:', err);
        return null;
      });
    },

    onTrigger(trigger) {
      const action = {
        timelineId: this.graphic.timeline_id,
        elementId: this.element.id,
        trigger,
      };

      if (this.isCurrentLayout) {
        this.$store.dispatch('sendAction', action);
      }

      if (this.isCurrentSampleLayout) {
        this.$store.dispatch('sendPreviewAction', action);
      }
    },

    updateField(field, value) {
      this.$emit('value', { field, value });
    },

    validateNumericInput(event) {
      const numericValue = event.target.value.replace(/[^0-9]/g, '');
      this.widgetId = numericValue;
      event.target.value = numericValue; // eslint-disable-line no-param-reassign
    },

    updateScorestreamUrl() {
      if (!this.isScorestream) {
        return;
      }

      const { widgetType, widgetId } = this;

      if (widgetType && widgetId && this.isValidWidgetId) {
        const baseUrl = process.env.GRAPHICS_SPA || 'https://graphic-builder.tagboard.com';
        const url = `${baseUrl}/scorestream.html?widgetType=${widgetType}&widgetId=${widgetId}`;
        this.updateField('url', url);
      }
    },

    selectAsset() {
      this.$store.dispatch('toggleAssetModal', { toggled: true, type: 'image' });

      window.assetmanfn = (asset) => {
        this.element.backgroundImageUrl = asset.media_url; // eslint-disable-line vue/no-mutating-props
        this.updateField('backgroundImageUrl', asset.media_url);
        this.$store.dispatch('toggleAssetModal', { toggled: false, type: 'image' });
      };
    },
  },
};
</script>
